import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Container } from "@/components/shared";
import { ILLUSTRATIONS } from "@/lib/profiles";
import TeenVoice from "@/pages/homes/TeenVoice";
import { Mic, Search, MessageSquare, Scale, Clapperboard, BookOpen, Users, ArrowRight } from "lucide-react";

const CARDS = [
  { id: "reply", to: "/reply", icon: MessageSquare },
  { id: "analyze", to: "/analyze", icon: Search },
  { id: "rights", to: "/rights", icon: Scale },
  { id: "create", to: "/create", icon: Clapperboard },
  { id: "library", to: "/bibliotheque", icon: BookOpen },
];

export default function TeenHome() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [voiceOpen, setVoiceOpen] = useState(false);
  useEffect(() => {
    if (new URLSearchParams(location.search).get("raconte") === "1") setVoiceOpen(true);
  }, [location.search]);
  const closeVoice = () => {
    setVoiceOpen(false);
    if (location.search.includes("raconte")) navigate("/", { replace: true });
  };
  const rise = { hidden: { opacity: 0, y: 20 }, show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.45 } }) };

  return (
    <Container className="max-w-4xl">
      <div data-testid="teen-home">
        {/* Hero */}
        <motion.section initial="hidden" animate="show" variants={rise} custom={0}
          className="relative overflow-hidden rounded-[1.75rem] mb-8 p-6 md:p-8"
          style={{ background: "linear-gradient(160deg,#0d0d10 40%,#0a1a0d)" }}>
          <div className="absolute -top-16 -right-10 w-72 h-72 rounded-full pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(57,255,20,0.22), transparent 65%)" }} />
          <div className="relative flex items-center gap-4">
            <div className="flex-1">
              <h1 className="font-display text-3xl md:text-5xl leading-[1.05] whitespace-pre-line" style={{ color: "var(--text-main)" }}>
                {t("teen.heroTitle")}
              </h1>
              <p className="text-white/70 text-sm mt-4 max-w-md">{t("teen.heroSub")}</p>
              <div className="flex flex-wrap gap-3 mt-5">
                <button data-testid="teen-cta-talk" onClick={() => setVoiceOpen(true)}
                  className="gold-gradient rounded-xl px-5 py-3 font-semibold inline-flex items-center gap-2 transition-transform duration-150 hover:scale-105"
                  style={{ color: "var(--accent-fg)" }}>
                  <Mic size={18} /> {t("teen.talkToAI")}
                </button>
                <button data-testid="teen-cta-analyze" onClick={() => navigate("/analyze")}
                  className="rounded-xl px-5 py-3 font-semibold inline-flex items-center gap-2 border transition-colors"
                  style={{ borderColor: "var(--accent)", color: "var(--accent)" }}>
                  <Search size={18} /> {t("teen.analyzeSituation")}
                </button>
              </div>
            </div>
            <img src={ILLUSTRATIONS.teenHero} alt="" className="hidden sm:block w-40 h-52 object-cover rounded-2xl shrink-0" data-testid="teen-hero-img" />
          </div>
        </motion.section>

        {/* Bandeau punchline */}
        <motion.div initial="hidden" animate="show" variants={rise} custom={1}
          className="card-surface p-4 mb-8 flex items-center gap-3">
          <span className="text-gold text-2xl font-display">"</span>
          <p className="font-display text-lg" style={{ color: "var(--text-main)" }}>{t("teen.punchline")}</p>
        </motion.div>

        {/* Grille de fonctions */}
        <h2 className="font-display text-xl mb-4" style={{ color: "var(--text-main)" }}>{t("teen.whatToDo")}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {CARDS.map((c, i) => (
            <motion.button key={c.id} data-testid={`teen-card-${c.id}`} onClick={() => navigate(c.to)}
              initial="hidden" animate="show" variants={rise} custom={i + 2}
              className="card-surface p-5 text-left flex flex-col min-h-[128px] group transition-transform duration-150 hover:scale-[1.03]">
              <span className="w-11 h-11 rounded-xl flex items-center justify-center mb-3"
                style={{ background: "color-mix(in srgb, var(--accent) 18%, transparent)", color: "var(--accent)" }}>
                <c.icon size={20} />
              </span>
              <p className="font-display text-base" style={{ color: "var(--text-main)" }}>{t(`teen.cards.${c.id}.title`)}</p>
              <p className="text-white/60 text-xs mt-1 flex-1">{t(`teen.cards.${c.id}.desc`)}</p>
              <span className="text-gold text-xs inline-flex items-center gap-1 mt-2 group-hover:gap-2 transition-all">{t("common.open")} <ArrowRight size={12} /></span>
            </motion.button>
          ))}
        </div>
      </div>

      <TeenVoice open={voiceOpen} onClose={closeVoice} />
    </Container>
  );
}
